/*
 * @license
 * Copyright© Celligence International, LLC - 2023
 * 
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 * 
 */

const SWMC_DOMAIN_NAME = "swmc";
const ANGELAI_DOMAIN_NAME = "angelai";
const CELLIGENCE_DOMAIN_NAME = "celligence";
const EVENT_TYPE_ANGELAI_NFT = "AngelAiNFT";
const EVENT_TYPE_THIRD_PARTY = "ThirdParty";
const EVENT_TYPE_NFT = "NFT";
const CDN_URL_RESOURCE = process.env.REACT_APP_CDN;
const EVENT_TEMPLATES_CDN_URL = CDN_URL_RESOURCE + "events-templates/";

/**
 * @author Pratik Kajrolkar 
 * @date 06/06/2024
 * 
 * Determines whether the device accessing the page is a mobile
 *
 * @return {String} Returns "Mobile" if the device is a mobile
 */
function isMobileDevice() {
    const width = window.innerWidth;
    const isMobile = width < 992; // Adjust the values as needed

    if (isMobile) {
        return true;
    } else {
        return false;
    }
}

function isMobileNotTabletDevice() {
    const width = window.innerWidth;
    const isMobile = width < 768; // Adjust the values as needed

    if (isMobile) {
        return true;
    } else {
        return false;
    }
}

/**
 * Trims leading and trailing whitespace from a string.
 *
 * @param {string} theStr - The input string to be trimmed.
 * @return {string} The trimmed string.
 */
function trim(theStr) {
    return theStr.replace(/^\s\s*/, "").replace(/\s\s*$/, "");
}

/**
 * Extracts numbers from a given input string.
 *
 * @param {string} inVal - The input string from which numbers are to be extracted.
 * @param {boolean} isDouble - Indicates whether to allow decimal points.
 * @param {string} removeCharsFromPrefix - Characters to be removed from the beginning of the extracted numbers.
 * @return {string} The extracted numbers as a string.
 */
function getNumbers(inVal, isDouble, removeCharsFromPrefix) {
    var retVal = "";
    var okChars = "0123456789";
    var tmpVal = trim(inVal);
    var curChar = "";
    var isDotFound = false;

    if (tmpVal !== "") {
        for (var i = 0; i < tmpVal.length; i++) {
            curChar = tmpVal.charAt(i);

            if (okChars.indexOf(curChar) !== -1) {
                if (!isDotFound && curChar === "." && isDouble) {
                    isDotFound = true;
                    retVal = retVal + ".";
                }

                if (retVal === "" && removeCharsFromPrefix.indexOf(curChar) !== -1) {
                    //Do nothing
                } else {
                    retVal += curChar;
                }
            }
        }
    }

    return retVal;
}

/**
 * Serializes a form into an object with key-value pairs, where the keys are the form element IDs 
 * and the values are the form element values.
 *
 * @param {HTMLFormElement} form - The form to be serialized.
 * @return {Object} An object containing the serialized form data.
 */
function serialize(form) {
    let inputs = form.elements;
    let testArray = {};
    for (let i = 0; i < inputs.length - 1; i++) {
        testArray[inputs[i].id] = inputs[i].value;
    }
    return testArray;
}

/**
 * Returns the current form URL based on the current window location. 
 * If the window location has a third and fourth segment, it returns the URL 
 * with the third segment and the fourth segment without the hash symbol. 
 * If the window location does not have a third and fourth segment, it returns 
 * the root URL ("/").
 *
 * @return {string} The current form URL.
 */
function getFormURL() {
    if (
        window.location.href.split("/")[3] &&
        window.location.href.split("/")[4]
    ) {
        const temp = window.location.href.split("/")[4].replace("#", "");
        return "/" + window.location.href.split("/")[3] + "/" + temp;
    } else {
        return "/";
    }
}

/**
 * Removes non-ASCII special characters and replaces all special characters with an empty string.
 *
 * @param {string} str - The input string to be processed.
 * @return {string} The processed string with non-ASCII special characters and special characters removed.
 */
function removeNonAsciiSpecialCharacters(str) {
    //check if pass value is null or blank.
    //return empty string if pass value is null or blank
    if (str === null || str === "") {
        return "";
    } else {
        //convert the pass value to string
        str = str.toString();
        //remove non-printable ASCII chars and replace it with empty string
        str = str.replace(/[^\x20-\x7E]/g, "");
        //replace all special characters with empty string
        str = str.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}0-9\[\]\\\/]/g, "");
        //return updated string to calling function
        return str;
    }
}

/**
 * Removes non-ASCII characters and disallowed special characters for address fields.
 * Allows specific special characters like ',', '.', '#', '-', and '/'.
 *
 * @param {string} str - The input address string to be processed.
 * @return {string} The processed string with non-ASCII and disallowed special characters removed.
 */
function processAddressField(str) {
    // Check if the input value is null or blank.
    if (str === null || str === "") {
        return "";
    }

    // Convert the input value to a string.
    str = str.toString();

    // Remove non-printable ASCII characters.
    str = str.replace(/[^\x20-\x7E]/g, "");

    // Remove all disallowed special characters, keeping ',', '.', '#', '-', and '/'.
    const regex = /[^a-zA-Z0-9 ,.#-/]/g;
    str = str.replace(regex, "");

    return str;
}

/**
 * Removes non-ASCII characters and disallowed special characters for comment fields.
 * Allows specific special characters like '.', ',', '!', '?', '(', ')', "'", '"', and '\n'.
 *
 * @param {string} str - The input comment string to be processed.
 * @return {string} The processed string with non-ASCII and disallowed special characters removed.
 */
function processCommentField(str) {
    // Check if the input value is null or blank.
    if (str === null || str === "") {
        return "";
    }

    // Convert the input value to a string.
    str = str.toString();

    // Remove non-printable ASCII characters.
    str = str.replace(/[^\x20-\x7E]/g, "");

    // Remove all disallowed special characters, keeping '.', ',', '!', '?', '(', ')', "'", '"', and '\n'.
    const regex = /[^a-zA-Z0-9 .,!?()'"\n]/g;
    str = str.replace(regex, "");

    return str;
}

/**
 * Removes non-ASCII characters and disallowed special characters for city fields.
 * Allows specific special characters like '-' and '.'.
 *
 * @param {string} str - The input city string to be processed.
 * @return {string} The processed string with non-ASCII and disallowed special characters removed.
 */
function processCityField(str) {
    // Check if the input value is null or blank.
    if (str === null || str === "") {
        return "";
    }

    // Convert the input value to a string.
    str = str.toString();

    // Remove non-printable ASCII characters.
    str = str.replace(/[^\x20-\x7E]/g, "");

    // Remove all disallowed special characters, keeping '-' and '.'.
    const regex = /[^a-zA-Z0-9 .-]/g;
    str = str.replace(regex, "");

    return str;
}

/**
 * Extracts the YouTube video ID from a given URL, including Shorts.
 *
 * @param {string} url - The URL of the YouTube video.
 * @return {string|null} The extracted YouTube video ID, or null if no match is found.
 */
function getYouTubeVideoId(url) {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
}

/**
 * @author Pratik Kajrolkar
 * @date 06/05/2024
 *
 * The below code will exposed the variable and functions written
 * under this javascript file to other javascript files when
 * this file is object is created under them.
 */
module.exports = {
    SWMC_DOMAIN_NAME,
    ANGELAI_DOMAIN_NAME,
    CELLIGENCE_DOMAIN_NAME,
    EVENT_TYPE_ANGELAI_NFT,
    EVENT_TYPE_THIRD_PARTY,
    EVENT_TYPE_NFT,
    CDN_URL_RESOURCE,
    EVENT_TEMPLATES_CDN_URL,
    isMobileDevice,
    isMobileNotTabletDevice,
    trim,
    getNumbers,
    serialize,
    getFormURL,
    removeNonAsciiSpecialCharacters,
    getYouTubeVideoId,
    processAddressField,
    processCommentField,
    processCityField
}
